import React from "react";
import { withStyles } from "@material-ui/styles";
import { I18n } from "@aws-amplify/core";
import {
  size,
  get,
  map,
  filter,
  includes,
  concat,
  sortBy,
  toLower,
} from "lodash";
import clsx from "clsx";
import Grid from "@material-ui/core/Grid";
import InputAdornment from "@material-ui/core/InputAdornment";
import IconButton from "@material-ui/core/IconButton";
import Checkbox from "@material-ui/core/Checkbox";
import TextField from "@material-ui/core/TextField";

import CustomButton from "./CustomButton";
import FormSection from "./FormSection";
import images from "../../assets/img/images";
import colors from "../../assets/style/colors";

const styles = {
  container: {
    padding: "0px 8px",
    outline: 0,
  },
  textField: {
    margin: 0,
    backgroundColor: "white",
  },
  menuList: {
    padding: "8px 0px",
  },
  placeholder: {
    fontStyle: "italic",
    colors: colors.black54,
    paddingTop: "9.5px",
    paddingBottom: "9.5px",
    fontSize: "0.875em",
  },
  input: {
    height: "36px",
  },
  selectInput: {
    paddingTop: "9.5px",
    paddingBottom: "9.5px",
    fontSize: "0.875em",
  },
  link: {
    cursor: "pointer",
    color: colors.primary,
    "&:hover": {
      textDecoration: "underline",
      backgroundColor: colors.white,
    },
  },
  checkboxRoot: {
    padding: "4px",
  },
  textFieldRoot: {
    "& .MuiOutlinedInput-root": {
      "&.Mui-focused fieldset": {
        borderColor: colors.primary,
        borderWidth: "1px",
      },
    },
  },
  option: {
    height: "36px",
    cursor: "pointer",
  },
};

const SearchableMultipleSelect = (props) => {
  const { classes, value, options, onChange, autoFocus = false } = props;

  const [searchValue, setSearchValue] = React.useState();
  const [sortedData, setSortedData] = React.useState();

  const [open, _setOpen] = React.useState(false);
  const openRef = React.useRef(open);

  const setOpen = (value) => {
    setTimeout(() => {
      openRef.current = value;
      _setOpen(value);
    }, 200);
  };

  const onOpen = (e) => {
    setOpen(true);
  };

  const onClose = () => {
    if (props.onClose) {
      props.onClose();
    }
    setOpen(false);
  };

  React.useEffect(() => {
    if (open) {
      setSortedData(
        sortBy(
          sortBy(options, (opt) => get(opt, ["name"])),
          (opt) => !includes(value, get(opt, ["id"]))
        )
      );
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  React.useEffect(() => {
    // Listen click outside the menu to close the dropdown
    window.addEventListener("click", (event) => {
      const menu = get(document.getElementsByClassName("results-paper"), [0]);
      if (openRef.current && menu && !menu.contains(event.target)) {
        onClose();
      }
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const setFormValue = (id, index, value) => {
    setSearchValue(value);
  };

  return (
    <TextField
      className={classes.textField}
      classes={{ root: classes.textFieldRoot }}
      select
      fullWidth
      value={[]}
      variant="outlined"
      margin="dense"
      InputProps={{
        className: classes.input,
      }}
      SelectProps={{
        classes: {
          root: !props.value ? classes.placeholder : classes.selectInput,
          select: classes.select,
        },
        MenuProps: {
          autoFocus: false,
          getContentAnchorEl: null,
          anchorOrigin: { vertical: "bottom", horizontal: "left" },
          MenuListProps: {
            classes: { root: classes.menuList },
            id: "selectMenuList",
          },
        },
        open: open,
        onOpen: onOpen,
        onClose: onClose,
        displayEmpty: true,
        multiple: true,
        renderValue: () => {
          if (size(value) === 0) return null;
          if (size(value) === 1)
            return get(
              filter(options, (opt) => get(opt, ["id"]) === get(value, [0])),
              [0, "name"]
            );
          if (size(value) === size(get(props, ["options"])))
            return I18n.get("common_all");
          return `(${size(value)} ${I18n.get("common_selected")})`;
        },
      }}
    >
      <Grid
        container
        direction="column"
        className={clsx(classes.container, "results-paper")}
      >
        <Grid item>
          <FormSection
            formValue={[searchValue || ""]}
            setFormValue={setFormValue}
            noLabel
            id="search"
            autoComplete="new-password"
            startAdornment={
              <img
                src={images.searchIcon}
                alt="search"
                width="17px"
                height="17px"
              />
            }
            noMarginBottom
            endAdornment={
              size(searchValue) > 0 && (
                <InputAdornment position="end">
                  <IconButton
                    edge="end"
                    onClick={() => setSearchValue()}
                    style={{ opacity: "0.54" }}
                  >
                    <img
                      src={images.closeIcon}
                      alt="search_close"
                      width="12px"
                      height="12px"
                    />
                  </IconButton>
                </InputAdornment>
              )
            }
            options={{
              autoFocus: autoFocus,
              placeholder: I18n.get("site_search"),
            }}
          />
        </Grid>
        <Grid item style={{ margin: "8px 0px" }}>
          <Grid container>
            <Grid item>
              <CustomButton
                color="primary"
                variant="contained"
                onClick={onClose}
              >
                {I18n.get("site_apply")}
              </CustomButton>
            </Grid>
            <Grid
              item
              xs
              style={{
                display: "flex",
                alignItems: "center",
                justifyContent: "flex-end",
              }}
            >
              <span
                className={classes.link}
                onClick={() =>
                  onChange({
                    target: {
                      value:
                        size(value) === size(options)
                          ? "selectNone"
                          : "selectAll",
                    },
                  })
                }
              >
                {size(value) === size(options)
                  ? I18n.get("profiles_deselectAll")
                  : I18n.get("profiles_selectAll")}
              </span>
            </Grid>
          </Grid>
        </Grid>
        {map(
          filter(
            sortedData,
            (opt) =>
              !searchValue ||
              toLower(get(opt, ["name"])).includes(toLower(searchValue))
          ),
          (opt) => (
            <Grid
              item
              key={get(opt, ["id"])}
              className={classes.option}
              onClick={() => {
                setSearchValue();
                onChange({
                  target: {
                    value: includes(value, get(opt, ["id"]))
                      ? filter(value, (item) => item !== get(opt, ["id"]))
                      : concat(value, [get(opt, ["id"])]),
                  },
                });
              }}
            >
              <Checkbox
                checked={includes(value, get(opt, ["id"]))}
                color="primary"
                classes={{ root: classes.checkboxRoot }}
              />
              <span>{get(opt, ["name"])}</span>
            </Grid>
          )
        )}
      </Grid>
    </TextField>
  );
};

export default withStyles(styles)(SearchableMultipleSelect);
